import React, { Fragment, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Button from '../shared/TailwindComponents/Button';
import Input from '../shared/TailwindComponents/Input';
import Textarea from '../shared/TailwindComponents/Textarea';
import Dropdown from '../shared/TailwindComponents/Dropdown';
import FieldShell from '../shared/TailwindComponents/FieldShell';
import { PiTextAaLight, PiTextAlignLeftLight, PiHashLight, PiCalendarLight, PiToggleLeftLight, PiCaretDownLight } from 'react-icons/pi';
import { Menu, Transition } from '@headlessui/react';

interface EditMatterDataModalContentProps {
  item?: { label: string; value: any; type?: string };
  onConfirm: (data: { label: string; value: string; type: string }) => void;
  onCancel: () => void;
}

const typeOptions = [
  { value: 'short_text', label: 'Short Text', icon: <PiTextAaLight className="h-5 w-5" /> },
  { value: 'long_text', label: 'Long Text', icon: <PiTextAlignLeftLight className="h-5 w-5" /> },
  { value: 'number', label: 'Number', icon: <PiHashLight className="h-5 w-5" /> },
  { value: 'date', label: 'Date', icon: <PiCalendarLight className="h-5 w-5" /> },
  { value: 'boolean', label: 'Yes / No', icon: <PiToggleLeftLight className="h-5 w-5" /> },
];

const TypeSelector: React.FC<{ value: string; onChange: (value: string) => void }> = ({ value, onChange }) => {
  const { t } = useTranslation();
  const selected = typeOptions.find(option => option.value === value) || typeOptions[0];
  return (
    <Menu as="div" className="relative inline-block text-start w-full">
      <Menu.Button as={Fragment}>
        {({ open }) => (
          <button className="inline-flex justify-between w-full rounded-md border border-gray-300 dark:border-gray-600 shadow-sm px-4 py-2 bg-white dark:bg-gray-700 text-sm font-medium text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500">
            <div className="flex items-center space-x-2">
              {selected.icon}
              <span>{t(selected.label)}</span>
            </div>
            <PiCaretDownLight
              className={`text-gray-500 transition-transform duration-300 ${open ? 'rotate-180' : ''}`}
            />
          </button>
        )}
      </Menu.Button>
      <Transition
        as={Fragment}
        enter="transition ease-out duration-100"
        enterFrom="transform opacity-0 scale-95"
        enterTo="transform opacity-100 scale-100"
        leave="transition ease-in duration-75"
        leaveFrom="transform opacity-100 scale-100"
        leaveTo="transform opacity-0 scale-95"
      >
        <Menu.Items className="origin-top-right absolute start-0 mt-2 w-full rounded-md shadow-lg bg-white dark:bg-gray-800 ring-1 ring-black ring-opacity-5 focus:outline-none z-10">
          <div className="py-1">
            {typeOptions.map(option => (
              <Menu.Item key={option.value}>
                {({ active }) => (
                  <button
                    onClick={() => onChange(option.value)}
                    className={`${active ? 'bg-gray-100 dark:bg-gray-700 text-gray-900 dark:text-gray-100' : 'text-gray-700 dark:text-gray-200'} group flex items-center w-full px-4 py-2 text-sm`}
                  >
                    {option.icon}
                    <span className="ms-2">{t(option.label)}</span>
                  </button>
                )}
              </Menu.Item>
            ))}
          </div>
        </Menu.Items>
      </Transition>
    </Menu>
  );
};

const EditMatterDataModalContent: React.FC<EditMatterDataModalContentProps> = ({ item, onConfirm, onCancel }) => {
  const { t } = useTranslation();
  const [label, setLabel] = useState(item?.label || '');

  // Safely convert any complex value (array, object) to a displayable string
  const normalizeValue = (val: any): string => {
    if (val === null || val === undefined) return '';
    if (typeof val === 'string') return val;
    if (typeof val === 'number' || typeof val === 'boolean') return String(val);
    if (Array.isArray(val)) {
      return val.map((v) => {
        if (typeof v === 'object' && v !== null) {
          if (v.name && v.role) return `${v.name} (${v.role})`;
          if (v.name) return v.name;
          return Object.values(v).join(': ');
        }
        return String(v);
      }).join(', ');
    }
    if (typeof val === 'object') {
      return Object.entries(val)
        .map(([k, v]) => `${k.replace(/_/g, ' ')}: ${v}`)
        .join(', ');
    }
    return String(val);
  };

  // Map API type values to our UI type options
  const resolveType = (type?: string): string => {
    if (!type) return 'short_text';
    if (['short_text', 'long_text', 'number', 'date', 'boolean'].includes(type)) return type;
    // API may return 'text' - map to long_text if value is long, otherwise short_text
    if (type === 'text') {
      const valStr = normalizeValue(item?.value);
      return valStr.length > 50 ? 'long_text' : 'short_text';
    }
    return 'short_text';
  };

  const [value, setValue] = useState(normalizeValue(item?.value));
  const [dataType, setDataType] = useState(resolveType(item?.type));

  const handleSave = () => {
    if (!label.trim()) {
      alert(t('Label cannot be empty'));
      return;
    }
    onConfirm({ label, value, type: dataType });
  };

  return (
    <div className="md:min-w-[600px] md:max-w-[600px]">
      <div className="space-y-4 px-4">
        <Input
          label={t('Label')}
          value={label}
          onChange={(e) => setLabel(e.target.value)}
          placeholder={t('Enter label')}
          className="text-sm"
          type="text"
          id="matter-data-label"
        />
        <FieldShell label={t('Type')} translate={false}>
          <TypeSelector value={dataType} onChange={setDataType} />
        </FieldShell>
        <FieldShell label={t('Value')} translate={false}>
          {dataType === 'long_text' ? (
            <Textarea
              id="matter-data-long-text"
              label=""
              value={value}
              onChange={(e) => setValue(e.target.value)}
              h="h-32"
              className="resize-none"
              placeholder={t('Enter value...')}
            />
          ) : dataType === 'boolean' ? (
            <Dropdown
              id="matter-data-boolean"
              value={value}
              onChange={(v) => setValue(v as string)}
              placeholder={t('Select...')}
              options={[
                { value: 'true', label: t('Yes') },
                { value: 'false', label: t('No') },
              ]}
            />
          ) : dataType === 'date' ? (
            <Input
              type="date"
              value={value}
              onChange={(e) => setValue(e.target.value)}
              className="mt-1"
              id="matter-data-date"
            />
          ) : dataType === 'number' ? (
            <Input
              type="number"
              value={value}
              onChange={(e) => setValue(e.target.value)}
              className="mt-1"
              placeholder={t('Enter number')}
              id="matter-data-number"
            />
          ) : (
            <Input
              type="text"
              value={value}
              onChange={(e) => setValue(e.target.value)}
              className="mt-1"
              placeholder={t('Enter value')}
              id="matter-data-value"
            />
          )}
        </FieldShell>
      </div>
      <div className="mt-6 flex justify-end gap-2 px-4 pt-6 border-t border-gray-200 dark:border-gray-600">
        <Button variant="neutral" size="small" onClick={onCancel}>
          {t('Cancel')}
        </Button>
        <Button 
          variant="primary" 
          size="small" 
          onClick={handleSave}
          disabled={!label.trim()}
        >
          {item ? t('Update') : t('Add')}
        </Button>
      </div>
    </div>
  );
};

export default EditMatterDataModalContent;